import { useContext, useRef } from 'react';

import { Box, Grid, GridItem } from '@chakra-ui/react';
import { Button, Card, CardContent, Icon, RequestFeedback, ShareFeedback } from '@mesh.ai/rabbit';
import OverviewSection from 'Deer/components/Overview/OverviewSection';
import { getUserL2Access } from 'Deer/helpers/getUserAccess';
import { useGetOverviewMetricsWithoutVisierToken } from 'Deer/hooks/dataHooks/metrics/useGetOverviewMetrics';
import useLocalStorageCards from 'Deer/hooks/useLocalStorageCards';
import { MetricCards } from 'Deer/metricCards/MetricCards';
import { ProfileContext } from 'Deer/pages/Profile/hooks/ProfileContext';
import { setIsCardOpen } from 'Deer/slice/DeerCollapsibleCard.slice';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useCFTableData } from 'screens/Levelup/LevelUpCF/hooks/dataHooks';
import { selectLoggedInEmployeeId } from 'selectors/loggedInEmployee';
import { checkNewStaticDataFeaturesEnabled } from 'utils/helpers/featureFlags';
import { getUniqueCardKey } from 'utils/helpers/getUniqueCardKey';

import { Praise } from 'assets/assetsV1/iconComponents';
import noInsights from 'assets/assetsV1/illustrations/deerIllustrations/noInsights.svg';

import { FEEDBACK_FORM_TYPE } from 'constants/ProjectConstants';
import { pushFeedbackParams } from 'constants/helper';
import { EVENT_SOURCES } from 'constants/trackingEvents';

import { EmptyScreen } from 'uiToolkitV1/EmptyScreen';
import { SkeletonLoader } from 'uiToolkitV1/SkeletonLoader';
import { TextHeader4 } from 'uiToolkitV1/Typography';

import { FeedbackSpiderChart } from './FeedbackSpiderChart';
import { FeedbackTable } from './FeedbackTable';
import FeedbackActions from './component/FeedbackActions';
import { useGetSpiderChartQuery } from './dataHooks';

const staticCards = [
  {
    title: 'No. of competencies with no progress in the last 30 days',
    value: 12,
  },
  { title: 'No. of feedback received in the last 30 days', value: 8 },
];

export const FeedbackOverview = () => {
  const loggedInEmployeeId = useSelector(selectLoggedInEmployeeId);
  const employeeContext = useContext(ProfileContext);
  const gridRef = useRef();
  const history = useHistory();
  const { data: spiderChartData, isLoading: iSpiderChartLoading } = useGetSpiderChartQuery(employeeContext?.id ?? null);
  const dispatch = useDispatch();
  const currentURL = history.location.pathname;

  // Generate the key using getUniqueCardKey function
  const localStorageKey = getUniqueCardKey({ url: currentURL, cardId: 'myDataCompetenciesOverview' });

  const isCardOpen = useLocalStorageCards({
    localStorageKey,
    card: 'competenciesOverview',
    section: 'myData',
  });

  const {
    data: cfTableData,
    isLoading: isCfTableLoading,
    isError: isCFTableDataError,
  } = useCFTableData({
    feedbackStatus: 'RECEIVED',
    limit: 1000,
    offset: 0,
    employeeId: employeeContext?.id ? employeeContext.id : null,
  });

  const { data } = useGetOverviewMetricsWithoutVisierToken<
    ['INDIVIDUAL_FEEDBACK_RECEIVED', 'INDIVIDUAL_COMPETENCIES_NO_PROGRESS']
  >({
    metrics: [
      {
        analyticsType: 'CORE_COMPETENCY',
        metricType: 'INDIVIDUAL_FEEDBACK_RECEIVED',
      },
      {
        analyticsType: 'CORE_COMPETENCY',
        metricType: 'INDIVIDUAL_COMPETENCIES_NO_PROGRESS',
      },
    ],
    employeeId: employeeContext?.id ? employeeContext.id : loggedInEmployeeId,
    showApiData: true,
  });

  const cardsData = [
    {
      title: 'No. of competencies with no progress in the last 30 days',
      value: data?.INDIVIDUAL_COMPETENCIES_NO_PROGRESS?.data,
    },
    { title: 'No. of feedback received in the last 30 days', value: data?.INDIVIDUAL_FEEDBACK_RECEIVED?.data },
  ];

  const handleRequestNewFeedbackClick = () => {
    // @ts-expect-error fix this
    pushFeedbackParams({
      history,
      formType: FEEDBACK_FORM_TYPE.REQUESTED,
      newForm: true,
      empId: (employeeContext?.id ? employeeContext.id : loggedInEmployeeId) ?? null,
      source: EVENT_SOURCES.FEEDBACK_PAGE.MY_FEEDBACK,
    });
  };

  const handleSharePublicPraiseClick = () => {
    // @ts-expect-error fix this
    pushFeedbackParams({
      history,
      formType: FEEDBACK_FORM_TYPE.PROVIDED,
      newForm: true,
      empId: (employeeContext?.id ? employeeContext.id : loggedInEmployeeId) ?? null,
      source: EVENT_SOURCES.FEEDBACK_PAGE.MY_FEEDBACK,
      isSharePublicPraise: true,
    });
  };

  const handleShareNewFeedbackClick = () => {
    // @ts-expect-error fix this
    pushFeedbackParams({
      history,
      formType: FEEDBACK_FORM_TYPE.PROVIDED,
      newForm: true,
      empId: (employeeContext?.id ? employeeContext.id : loggedInEmployeeId) ?? null,
      source: EVENT_SOURCES.FEEDBACK_PAGE.MY_FEEDBACK,
    });
  };

  const feedbackActionsOptions = [
    {
      label: 'Share public praise',
      icon: <Praise fill="transparent" className="mr-2 w-6 h-6" />,
      onClick: handleSharePublicPraiseClick,
    },
    {
      label: 'Request feedback',
      icon: <Icon IconSVG={RequestFeedback} className="mr-2 w-6 h-6" />,
      onClick: handleRequestNewFeedbackClick,
    },
    {
      label: 'Share feedback',
      icon: <Icon IconSVG={ShareFeedback} className="mr-2 w-6 h-6" />,
      onClick: handleShareNewFeedbackClick,
    },
  ];

  const handleChange = (newVal: boolean) => {
    dispatch(
      setIsCardOpen({
        card: 'competenciesOverview',
        section: 'myData',
        isCardOpen: newVal,
        localStorageKey,
      })
    );
    return newVal;
  };

  if (isCfTableLoading) {
    return <SkeletonLoader />;
  }

  if (isCFTableDataError) {
    return (
      <Card>
        <CardContent>
          <TextHeader4>
            Unable to load feedback data. Please try refreshing the page or contact support if the issue persists.
          </TextHeader4>
        </CardContent>
      </Card>
    );
  }

  return (
    <OverviewSection
      open={isCardOpen}
      onOpenChange={handleChange}
      subTitle="Your recent spike in feedback exchanges is commendable, especially on 'Customer Centricity.' However, it's essential to gather broader competency feedback. Consider seeking input on 'Collaboration' for a well-rounded perspective. Keep up the great work!"
      subTitleColor="warning"
      rightComp={
        employeeContext?.id && employeeContext?.id !== loggedInEmployeeId ? (
          <FeedbackActions label="Share/Request feedback" options={feedbackActionsOptions} />
        ) : (
          <Button iconLeft={RequestFeedback} onClick={handleRequestNewFeedbackClick} variant="outline">
            Request feedback
          </Button>
        )
      }
      title="Feedback"
      showCaption={cfTableData?.values.length > 0 && checkNewStaticDataFeaturesEnabled() && !employeeContext}
    >
      {getUserL2Access().hasIDPAccess && getUserL2Access().hasFeedbackAccess ? (
        <Grid width="full" minHeight="full" templateColumns={{ base: '1fr', md: '2fr 3fr' }} gap="8px">
          <GridItem>
            <Box height="full">
              {!spiderChartData?.competencyDetails?.length ? (
                <Card
                  style={{
                    minHeight: '418px',
                    height: '100%',
                  }}
                >
                  <CardContent>
                    <EmptyScreen
                      illustrationSrc={noInsights}
                      heading="Not enough data to show"
                      isSmallTextSize
                      description="Insights on your relative performance across the company 
          would appear here over time and with check-ins"
                      smallTextMargin="0px"
                      illustrationStyle={{
                        marginTop: '80px',
                      }}
                    />
                  </CardContent>
                </Card>
              ) : (
                <FeedbackSpiderChart iSpiderChartLoading={iSpiderChartLoading} spiderChartData={spiderChartData} />
              )}
            </Box>
          </GridItem>
          <GridItem rowSpan={2}>
            <Box height="full">
              <FeedbackTable gridRef={gridRef} cfTableData={cfTableData?.values} />
            </Box>
          </GridItem>
          <GridItem>
            <Box height="full">
              <MetricCards
                height="100%"
                columns={`repeat(${cardsData?.length}, 1fr)`}
                cardTitle="Metrics"
                cards={checkNewStaticDataFeaturesEnabled() ? staticCards : cardsData}
              />
            </Box>
          </GridItem>
        </Grid>
      ) : (
        <Grid width="full" minHeight="full" gap="8px">
          <GridItem>
            <Box height="full">
              <FeedbackTable gridRef={gridRef} cfTableData={cfTableData?.values} />
            </Box>
          </GridItem>
        </Grid>
      )}
    </OverviewSection>
  );
};
